import { isNullish } from '@y42/util/nullish';
import * as go from 'gojs';
import { withTransaction } from '../../../../../utils/with-transaction';
import { ExtendedDiagram } from '../../../types/extended-diagram';
import { ATTENTION_NODE_PREDICATES } from './../config/part-names';
import { attachAttentionNode, detachAttentionNode, findAttachedAttentionNodes } from './attention-utils';

export const attentionOverrideDraggingTool = (diagram: go.Diagram, category: string) => {
  const tool = diagram.toolManager.draggingTool;
  overrideDragging(category, tool);
};

const overrideDragging = (attentionCategory: string, tool: go.DraggingTool) => {
  const doDropOntoBase = tool.doDropOnto.bind(tool);
  const computeEffectiveCollectionBase = tool.computeEffectiveCollection.bind(tool);

  tool.computeEffectiveCollection = computeEffectiveCollection(tool, computeEffectiveCollectionBase, attentionCategory);
  tool.doDropOnto = doDropOnto(tool, doDropOntoBase, attentionCategory);
};

const computeEffectiveCollection =
  (
    tool: go.DraggingTool,
    computeEffectiveCollectionBase: (
      parts: go.Iterable<go.Part>,
      options: go.DraggingOptions,
    ) => go.Map<go.Part, go.DraggingInfo>,
    attentionCategory: string,
  ) =>
  (parts: go.Iterable<go.Part>, options: go.DraggingOptions): go.Map<go.Part, go.DraggingInfo> => {
    const collection = computeEffectiveCollectionBase(parts, options);
    const attachedAttentionNodes = findAttachedAttentionNodes(tool.diagram.nodes, attentionCategory);

    const attentionCollection = new go.Map<go.Part, go.DraggingInfo>();
    collection
      .filter(({ key: part }) => !!part.key && part.key in attachedAttentionNodes)
      .each(({ key: part }) =>
        attentionCollection.add(
          attachedAttentionNodes[part.key!],
          new go.DraggingInfo(attachedAttentionNodes[part.key!].location),
        ),
      );

    collection.addAll(attentionCollection);

    return collection;
  };

const doDropOnto =
  (
    tool: go.DraggingTool,
    doDropOntoBase: (point: go.Point, object: go.GraphObject) => void,
    attentionCategory: string,
  ) =>
  (point: go.Point, object: go.GraphObject) => {
    doDropOntoBase.call(tool, point, object);
    if (!tool?.draggedParts) {
      return;
    }

    const attentionNode =
      tool.draggedParts.count === 1 &&
      tool.draggedParts.filter((d) => d.key.category === attentionCategory)?.first()?.key;

    if (isNullish(attentionNode) || !(attentionNode instanceof go.Node)) {
      return;
    }

    const predicates =
      ATTENTION_NODE_PREDICATES in tool.diagram
        ? (tool.diagram as ExtendedDiagram)[ATTENTION_NODE_PREDICATES] || ([] as Array<(node: go.Node) => boolean>)
        : [];
    if (
      object?.part instanceof go.Node &&
      predicates.every((p: (node: go.Node) => boolean) => p(object.part as go.Node))
    ) {
      withTransaction(tool.diagram, () => attachAttentionNode(object.part!, attentionNode), 'Attach Attention');
      return;
    }

    if (isNullish(object?.part)) {
      withTransaction(tool.diagram, () => detachAttentionNode(attentionNode), 'Detach Attention');
      return;
    }
  };
